import 'react-app-polyfill/stable'
import 'core-js'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { ChakraProvider, ColorModeScript, extendTheme } from '@chakra-ui/react'
import { ClientUserProvider } from './contexts/ClientUserProvider'
import { DashboardProvider } from './contexts/DashboardProvider'
import { BillProvider } from './contexts/BillProvider'
import { ReportProvider } from './contexts/ReportProvider'
import App from './App'
import { Provider } from 'react-redux'
import store from './store'
import './i18n'
import { ApolloProvider } from '@apollo/client'
import { apolloClient } from 'lib/apollo'
import { CommonProvider } from 'contexts/CommonProvider'
import { PaymentHistoryProvider } from 'contexts/PaymentHistoryProvider'

// Modern Blue Professional Theme Configuration
const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  colors: {
    // Modern Blue Professional Color Palette
    brand: {
      50: '#eff6ff',   // Very light blue
      100: '#dbeafe',  // Light blue
      200: '#bfdbfe',  // Lighter blue
      300: '#93c5fd',  // Light blue
      400: '#60a5fa',  // Medium light blue
      500: '#3b82f6',  // Accent blue
      600: '#2563eb',  // Primary blue
      700: '#1d4ed8',  // Darker blue
      800: '#1e40af',  // Secondary blue
      900: '#1e3a8a',  // Dark blue
    },
    // Custom semantic colors
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',  // Main primary
      700: '#1d4ed8',
      800: '#1e40af',  // Secondary
      900: '#1e3a8a',
    },
    success: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',  // Main success
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
    warning: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',  // Main warning
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
    },
    error: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',  // Main error
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    // Background colors
    background: {
      light: '#f8fafc',    // Slate 50
      dark: '#0f172a',     // Slate 900
      card: {
        light: '#ffffff',
        dark: '#1e293b',   // Slate 800
      },
      sidebar: {
        light: '#ffffff',
        dark: '#1e293b',
      },
      header: {
        light: '#ffffff',
        dark: '#1e293b',
      },
    },
  },
  styles: {
    global: (props) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'background.dark' : 'background.light',
        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      },
      // Scrollbar styling
      '*::-webkit-scrollbar': {
        width: '8px',
      },
      '*::-webkit-scrollbar-track': {
        bg: props.colorMode === 'dark' ? 'gray.800' : 'gray.100',
      },
      '*::-webkit-scrollbar-thumb': {
        bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.400',
        borderRadius: '4px',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        bg: props.colorMode === 'dark' ? 'gray.500' : 'gray.500',
      },
    }),
  },
  components: {
    // Button component customization
    Button: {
      variants: {
        solid: (props) => ({
          bg: props.colorScheme === 'brand' ? 'primary.600' : undefined,
          color: props.colorScheme === 'brand' ? 'white' : undefined,
          _hover: {
            bg: props.colorScheme === 'brand' ? 'primary.700' : undefined,
            _disabled: {
              bg: props.colorScheme === 'brand' ? 'primary.600' : undefined,
            },
          },
          _active: {
            bg: props.colorScheme === 'brand' ? 'primary.800' : undefined,
          },
        }),
      },
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    // Card component customization
    Card: {
      baseStyle: (props) => ({
        container: {
          bg: props.colorMode === 'dark' ? 'background.card.dark' : 'background.card.light',
          borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
          boxShadow: props.colorMode === 'dark' 
            ? '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)'
            : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      }),
    },
    // Link component customization
    Link: {
      baseStyle: {
        color: 'primary.600',
        _hover: {
          color: 'primary.700',
          textDecoration: 'underline',
        },
      },
    },
    // Input component customization for better contrast
    Input: {
      variants: {
        outline: (props) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'white',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
            color: props.colorMode === 'dark' ? 'white' : 'gray.900',
            _placeholder: {
              color: props.colorMode === 'dark' ? 'gray.400' : 'gray.500',
            },
            _hover: {
              borderColor: props.colorMode === 'dark' ? 'primary.400' : 'primary.300',
            },
            _focus: {
              borderColor: 'primary.500',
              boxShadow: '0 0 0 1px',
              boxShadowColor: 'primary.500',
            },
          },
        }),
      },
      defaultProps: {
        variant: 'outline',
      },
    },
  },
  semanticTokens: {
    colors: {
      'chakra-body-bg': {
        _light: 'background.light',
        _dark: 'background.dark',
      },
      'chakra-border-color': {
        _light: 'gray.200',
        _dark: 'gray.700',
      },
    },
  },
})

createRoot(document.getElementById('root')).render(
  <>
    <ColorModeScript initialColorMode={theme.config.initialColorMode} />
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <ChakraProvider theme={theme}>
          <ClientUserProvider>
            <DashboardProvider>
              <BillProvider>
                <ReportProvider>
                  <PaymentHistoryProvider>
                    <CommonProvider>
                      <App />
                    </CommonProvider>
                  </PaymentHistoryProvider>
                </ReportProvider>
              </BillProvider>
            </DashboardProvider>
          </ClientUserProvider>
        </ChakraProvider>
      </ApolloProvider>
    </Provider>
  </>,
)
